import { FileText, Play, Tag, Users, type LucideIcon } from '@/lib/icons';
import type { TileTone } from '@/components/ui/icon-tile';
import type { ReviewKind } from '@/services/review';

/** URL segment ↔ kind. The URL reads like the page: /review/prices. */
export const KIND_SLUGS = ['influencers', 'prices', 'contracts', 'content'] as const;
export type KindSlug = (typeof KIND_SLUGS)[number];

export const SLUG_TO_KIND: Record<KindSlug, ReviewKind> = {
  influencers: 'influencer',
  prices: 'negotiation',
  contracts: 'contract',
  content: 'content',
};
export const KIND_TO_SLUG: Record<ReviewKind, KindSlug> = {
  influencer: 'influencers',
  negotiation: 'prices',
  contract: 'contracts',
  content: 'content',
};

/**
 * A stage's address under its kind's own name ("/review/influencer"): the stage it lands on ("influencers"), else null
 * (a seat without the stage then reads its role refusal, never "That page doesn't exist").
 */
export const slugOfKindName = (segment: string): KindSlug | null => (Object.hasOwn(KIND_TO_SLUG, segment) ? KIND_TO_SLUG[segment as ReviewKind] : null);

export interface KindMeta {
  /** "Review influencers" — the kind's name in sentences and lists (managed-real's `managedReviewCopy`). */
  title: string;
  /** "Review Influencers" — the stage's name on the review page's stage dock (managed-real's `REVIEW_TABS`). */
  lane: string;
  /** One of them: "influencer", "price", "contract", "draft". */
  noun: string;
  nounPlural: string;
  /** The kind's tile, as the prototype draws it everywhere (`KIND_TILE`). */
  icon: LucideIcon;
  tone: TileTone;
  /** Its items are prices: a seat that can't see prices sees the stage by name, with no count (`countsFor`). */
  money?: true;
}

export const KIND_META: Record<ReviewKind, KindMeta> = {
  negotiation: { title: 'Review prices', lane: 'Review Prices', noun: 'price', nounPlural: 'prices', icon: Tag, tone: 'orange', money: true },
  contract: { title: 'Review contracts', lane: 'Review Contract', noun: 'contract', nounPlural: 'contracts', icon: FileText, tone: 'blue' },
  content: { title: 'Review content', lane: 'Review Content', noun: 'draft', nounPlural: 'drafts', icon: Play, tone: 'purple' },
  influencer: { title: 'Review influencers', lane: 'Review Influencers', noun: 'influencer', nounPlural: 'influencers', icon: Users, tone: 'green' },
};

/**
 * Whether a stage shows how many wait in it to this seat. Review Prices, to a seat that can't see prices, reads by name
 * alone ("Review Prices", no count, no faces): it stays a working door, never greyed, so the page is still reachable,
 * but it never promises work there that the seat can't do.
 */
export const countsFor = (kind: ReviewKind, seesMoney: boolean): boolean => seesMoney || !KIND_META[kind].money;
